import React, { Component, Context } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import FullCalendar from "@fullcalendar/react";
import { EventInput, EventApi } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction"; // needed for dayClick
import { addMinutes } from "date-fns";
import EventIcon from "@material-ui/icons/Event";
import TimeIcon from "@material-ui/icons/AccessTime";
import tippy from "tippy.js";
import { IEvent } from "../../laco-common/base/models/event";
import AuthContext from "../../laco-common/auth/AuthContext";
import { IEventActionDispatches, mapEventDispatchToProps } from "../../redux/actions/event";
import { IEventState, mapEventStateToProps } from "../../redux/reducers/event";
import { WidgetProps } from "../widgets";
import { DateInput } from "../../forms/InputComponents";
import UIModal from "../ui/UIModal";
import UserRoleContent from "../auth/permissions/UserRoleContent";
import { displayDateTime2, displayShortStartToEnd } from "../../laco-common/helpers";
import LinkifyText from "../../laco-common/components/LinkifyText";
import DescriptionText from "../../laco-common/components/DescriptionText";
import { FONT_FAMILY } from "../../theme";
import PlatformUtils from "../../utils/platform";
import { waitForElement } from "../../utils";
import { locationLink } from "../../models/googleMaps";
import { IAuthContext } from "../../laco-common/base/auth";
import ImageGalleryPaging from "../market/ImageGalleryPaging";
import { IImage } from "../../models/Image";
import { FileType } from "../../laco-common/base/models/IFile";
import ClientFirebase from "../../laco-common/firebase/web";
import "./EventsCalendar.scss";
import "tippy.js/dist/tippy.css"; // optional for styling

interface EventCalendarState {
	calendarWeekends: boolean;
	calendarEvents: EventInput[];
	openEvent: IEvent;
	selectedDate: any;
}

class EventsCalendar extends Component<
	IEventActionDispatches & IEventState & WidgetProps & RouteComponentProps,
	EventCalendarState
> {
	static contextType: Context<IAuthContext> = AuthContext;
	private routeKey: string;
	private _historySub = null;
	calendarComponentRef = React.createRef<FullCalendar>();

	state = {
		calendarWeekends: true,
		calendarEvents: this.convertToCalendarEvents(this.props.events),
		openEvent: undefined,
		selectedDate: new Date(),
	};

	componentDidMount() {
		this._historySub = this.props.history.listen((location, action) => {
			this.init(true);
		});
		this.init();
		waitForElement(".fc-fullList-button", (el) => {
			el.addEventListener("click", () => {
				this.setSelectedDate(Date.UTC(new Date().getFullYear(), new Date().getMonth()));
			});
		});
	}

	componentWillUnmount() {
		this._historySub();
	}

	async init(fromRouter?: boolean) {
		const newRouteKey: string = this.props.location.key;
		if (fromRouter && newRouteKey === this.routeKey) return;
		this.routeKey = newRouteKey;
		const param: any = this.props.match.params ? this.props.match.params["id"] : undefined;
		Promise.all([this.props.loadGroups(), this.props.loadEvents()]).then((results) => {
			const events: IEvent[] = results[1];
			console.log("Events Calendar events loaded:", events);
			this.setState({ calendarEvents: this.convertToCalendarEvents(events) });
			if (param) {
				if (isNaN(param)) {
					this.openEvent(param);
				} else {
					this.setSelectedDate(param);
				}
			}
		});
	}

	convertToCalendarEvents(events: IEvent[]): EventInput[] {
		if (!events || !events.length) return [];
		const calendarEvents: EventInput[] = [];
		events.forEach((e: IEvent) => {
			const { id, title, link, group_id, description } = e;
			const start: Date = new Date(e.eventDate);
			const end: Date = addMinutes(start, e.eventDuration as number);
			calendarEvents.push({ id, title, start: e.eventDate, end, hasEnd: true, groupId: group_id });
		});
		console.log("calendarEvents:", calendarEvents);
		return calendarEvents;
	}

	openEvent(id: string) {
		const event: IEvent = this.props.events.find((e: IEvent) => e.id === id);
		if (event) {
			this.setSelectedDate(event.eventDate as number);
		}
		const images: IImage[] = [];
		(event.images || []).forEach((filename: string, index: number) => {
			const imageUrl: string = ClientFirebase.getStoragePath("messages", event.id, filename);
			images.push({
				title: event.title,
				desc: event.description,
				url: imageUrl,
				filename,
				fileType: FileType.IMAGE,
				order: index,
				preview: false,
			});
		});
		event.media = images;
		this.setState({ openEvent: event });
	}

	setSelectedDate(datetime: number) {
		const openDate: Date = new Date(Number(datetime));
		this.setState({ selectedDate: openDate });
		this.gotoDate(openDate.toISOString());
	}

	// toggleWeekends = () => {
	// 	this.setState({
	// 		// update a property
	// 		calendarWeekends: !this.state.calendarWeekends,
	// 	});
	// };

	// gotoPast = () => {
	// 	let calendarApi = this.calendarComponentRef.current!.getApi();
	// 	calendarApi.gotoDate("2000-01-01"); // call a method on the Calendar object
	// };

	gotoDate = (date: string | Date) => {
		let calendarApi = this.calendarComponentRef.current!.getApi();
		calendarApi.gotoDate(date);
	};

	handleDateClick = (arg: any) => {
		console.log("handleDateClick:", arg);
		// if (confirm("Would you like to add an event to " + arg.dateStr + " ?")) {
		// this.setState({
		// 	// add new event data
		// 	calendarEvents: this.state.calendarEvents.concat({
		// 		// creates a new array
		// 		title: "New Event",
		// 		start: arg.date,
		// 		allDay: arg.allDay,
		// 	}),
		// });
		// }
	};

	onEventClick(e: { event: EventApi }) {
		console.log("onEventClick:", e, e.event.id);
		this.openEvent(e.event.id);
	}

	onEventMouseOver(info) {
		console.log("onEventMouseOver:", info);
		if (info.el.classList.contains("fc-list-item")) return;
		tippy(`[data-event-${info.event.id}]`, {
			content: `${displayShortStartToEnd(info.event.start, info.event.end)} ${info.event.title}`,
			placement: "right-end",
			popperOptions: {
				placement: "right-end",
			},
		});
	}

	renderEvent(info) {
		console.log("renderEvent info:", info);
		// info.el.setAttribute("data-tippy-content", "yo ");
		const el: HTMLAnchorElement = info.el;
		el.setAttribute(`data-event-${info.event.id}`, info.event.id);
		const contentEl: HTMLDivElement = el.firstChild as any;
		const color: string = (this.props.groupsColorsMap || {})[info.event.groupId] || "black";
		if (color) {
			contentEl.style.backgroundColor = color;
		}
	}

	render() {
		const openEvent: IEvent = this.state.openEvent;
		// const today: Date = new Date();
		// let lastYear: any = new Date(Date.UTC(today.getFullYear() - 1, 0)); // new Date(today.getFullYear() - 1);
		// let nextYear: any = new Date(Date.UTC(today.getFullYear() + 1, 0)); //new Date(today.getFullYear() + 1);

		// lastYear = "01/01/2017";
		// nextYear = "01/01/2020";

		// console.log(" lastYear, nextYear:", lastYear, nextYear, lastYear === nextYear);

		return (
			<div className="events-calendar-container container section-padding py-4 mt-1">
				<div className="events-calendar-header d-flex justify-content-start">
					{/* <button onClick={this.toggleWeekends}>toggle weekends</button>&nbsp; */}
					{/* <button onClick={this.gotoPast}>go to a date in the past</button>&nbsp; (also, click a date/time to
					add an event) */}
					<div className="goToDateInput">
						<DateInput
							field={{
								label: "Go to Date",
								value: null, // this.state.selectedDate,
								isDate: true,
								order: 0,
								valid: true,
							}}
							onChange={(d: Date) => {
								this.setState({ selectedDate: d });
								this.gotoDate(d);
							}}
						/>
					</div>
				</div>
				<div className="events-calendar">
					<FullCalendar
						defaultView="dayGridMonth" // listWeek dayGridMonth
						views={{
							dayGridMonth: {
								slotLabelFormat: {
									hour: "numeric",
									minute: "2-digit",
									omitZeroMinute: true,
									meridiem: "long",
								},
							},
							fullList: {
								type: "listWeek",
								duration: { year: 2 },
								snapDuration: { years: 2 },
								nowIndicator: true,
								// minTime: Date.UTC(today.getFullYear() - 1, 0),
								// visibleRange: { start: lastYear, end: nextYear },
								// validRange: {
								// 	start: lastYear,
								// 	end: nextYear,
								// },
								buttonText: "List All",
								displayEventTime: true,
								displayEventEnd: true,
								// defaultDate: new Date("01/01/2000"),
								// dayPopoverFormat
								// navLinkDayClick
								// navLinkWeekClick
								// selectable
								// dayPopoverFormat
								// dateClick
							},
							timeGridWeek: {
								type: "timeGridWeek",
								nowIndicator: true,
							},
							timeGridDay: {
								type: "timeGridDay",
								nowIndicator: true,
							},
						}}
						header={{
							left: "prev,next today",
							center: "title",
							right: "dayGridMonth,timeGridWeek,timeGridDay,fullList", // fullList listWeek
						}}
						plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
						ref={this.calendarComponentRef}
						eventLimit={true}
						dayPopoverFormat={{ month: "long", day: "numeric", year: "numeric" }}
						weekends={this.state.calendarWeekends}
						events={this.state.calendarEvents}
						// dateClick={this.handleDateClick}
						eventRender={this.renderEvent.bind(this)}
						eventClick={this.onEventClick.bind(this)}
						eventMouseEnter={this.onEventMouseOver.bind(this)}
					/>
				</div>
				{!!openEvent && (
					<UIModal
						title={
							<>
								<EventIcon style={{ fontSize: "1rem" }} className="mr-1" />
								{openEvent.title}
							</>
						}
						pageTitle={openEvent.title}
						open={!!openEvent}
						onClose={() => this.setState({ openEvent: undefined })}
						showCloseIcon={true}
						style={{ maxWidth: "600px" }}
						noPadding={true}
						className="pb-4 single-event-container"
					>
						<div style={{ fontSize: "0.8rem" }}>
							<TimeIcon style={{ fontSize: "0.8rem", marginRight: "0.4rem", marginLeft: "0.05rem" }} />
							{displayDateTime2(openEvent.eventDate as number, openEvent.eventDuration as number)}
							<UserRoleContent noAuthMessage={true}>
								{locationLinkDisplay(openEvent.eventLocation)}
							</UserRoleContent>
						</div>

						<ImageGalleryPaging images={openEvent.media} />

						<div style={{ clear: "both" }} />

						<UserRoleContent roles={["user", "admin"]} authMessageSuffix="event details" className="mt-3">
							{!!openEvent.description && (
								<>
									<h4 className="mt-4 mb-0">Description</h4>
									<DescriptionText style={{ fontFamily: FONT_FAMILY }} text={openEvent.description} />
								</>
							)}
							{openEvent.link && (
								<a className="d-block mt-2 text-md" href={openEvent.link} target="_blank">
									{openEvent.link_text || "Go to Link >"}
								</a>
							)}
						</UserRoleContent>
					</UIModal>
				)}
			</div>
		);
	}
}

export default connect<IEventState, IEventActionDispatches>(
	mapEventStateToProps,
	mapEventDispatchToProps
)(withRouter(EventsCalendar));

export const locationLinkDisplay = (location: string) => {
	if (!location) return undefined;
	const locationUrl = locationLink(location);
	return (
		<a
			className="text-xs d-flex align-items-center"
			style={{ marginLeft: "0.15rem" }}
			href={locationUrl}
			target="_blank"
		>
			<i className="im im-map-o mr-1 text-xxs" style={{ marginTop: "4px" }}></i> <span>{location}</span>
		</a>
	);
};

// _handlePressDirections = () => {
//     let { address, postalCode, city } = this.props.userRoute;

//     let daddr = encodeURIComponent(`${address} ${postalCode}, ${city}`);

//     if (Platform.OS === 'ios') {
//       Linking.openURL(`http://maps.apple.com/?daddr=${daddr}`);
//     } else {
//       Linking.openURL(`http://maps.google.com/?daddr=${daddr}`);
//     }
//   };
// }
